// frontend/src/pages/Amazon.js
// Amazon eligibility + push page (2026-09-15). Every in-stock product gets a
// gate decision (eligible / reason) from amazonEligibilityService; eligible
// items can be pushed one at a time or all at once. The Amazon price is always
// master × 1.10. Listing status comes from the channel_listings 'amazon' row:
// pending = submitted, waiting for Amazon; active = BUYABLE; error = rejected.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../App';

const REASON_LABELS = {
  eligible:                  'Eligible',
  no_valid_upc:              'No valid UPC',
  placeholder_upc:           'Placeholder UPC (233/234/400…)',
  no_asin:                   'Not in Amazon catalog',
  amazon_restricted:         'Amazon restriction (gated / not eligible)',
  condition_unknown:         'Condition unknown',
  condition_not_allowed_in:  'Condition not allowed in this category',
  clothing_excluded:         'Clothing (excluded by policy)',
  worn_shoes:                'Worn shoes (only new / like-new shoes)',
  title_missing_or_damaged:  'Title says missing / damaged',
  blocklist_brand:           'Brand / IP blocklist',
  blocklist_sku:             'SKU blocklist',
  variation_parent:          'Variation parent',
  refurbished_or_parts_not_listed: 'Refurbished / parts (not listed)',
  condition_unrecognized:    'Condition not recognized',
  no_price:                  'No price',
  title_mismatch_review:     'Title ≠ Amazon item (check UPC)',
};
function reasonKey(r) { return (r || '').split(':')[0]; }
function reasonLabel(r) { return REASON_LABELS[reasonKey(r)] || r; }
const COND_LABELS = { new_new: 'New', used_like_new: 'Used – Like New', used_very_good: 'Used – Very Good', used_good: 'Used – Good', used_acceptable: 'Used – Acceptable' };
function fmtTime(iso) { return iso ? new Date(iso + (iso.endsWith('Z') ? '' : 'Z')).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' }) : '—'; }

export default function Amazon() {
  const [data, setData]       = useState(null);
  const [view, setView]       = useState('eligible');     // eligible | listed | skipped | all
  const [reason, setReason]   = useState('all');
  const [search, setSearch]   = useState('');
  const [busy, setBusy]       = useState(null);
  const [msg, setMsg]         = useState(null);
  const [priceEdit, setPriceEdit] = useState(null);   // { sku, value }
  async function savePrice() {
    if (!priceEdit) return;
    setBusy('price');
    try {
      const r = await api.patch(`/amazon/price/${encodeURIComponent(priceEdit.sku)}`, { price: priceEdit.value.trim() === '' ? null : priceEdit.value });
      setMsg(`${priceEdit.sku}: Amazon price ${r.data.amazon_price != null ? 'set to $' + Number(r.data.amazon_price).toFixed(2) : 'back to automatic (master × 1.10 = $' + Number(r.data.effective).toFixed(2) + ')'}${r.data.pushed ? ' — pushed to Amazon' : r.data.pushError ? ' — push failed: ' + r.data.pushError : ''}`);
      setPriceEdit(null); await load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
    finally { setBusy(null); }
  }

  function load() { return api.get('/amazon/eligibility').then(r => setData(r.data)); }
  useEffect(() => { load(); }, []);
  useEffect(() => {   // keep polling while a run / pending submissions are in flight
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  async function runGate(force) {
    if (!window.confirm(force ? 'Re-check EVERY in-stock item against Amazon (catalog + restrictions)? Takes ~10 min.' : 'Run the eligibility gate over all in-stock items?')) return;
    setBusy('gate');
    try { await api.post('/amazon/eligibility/run', { force }); setMsg('Gate running in the background — this page refreshes every 20s.'); }
    catch (e) { setMsg('Failed to start: ' + (e.response?.data?.error || e.message)); }
    finally { setBusy(null); }
  }
  async function push(skus, label) {
    if (!window.confirm(`Push ${label} to Amazon now?\nPrice = master × 1.10, qty = master qty.`)) return;
    setBusy('push');
    try {
      const r = await api.post('/amazon/push', skus ? { skus } : {});
      const bad = r.data.results.filter(x => !x.ok);
      setMsg(`Submitted ${r.data.ok} of ${r.data.count}. ${bad.length ? 'Failed: ' + bad.map(b => `${b.sku} (${b.msg})`).join('; ') : 'Amazon processes offers asynchronously — status flips to Live on the next listing sync.'}`);
      await load();
    } catch (e) { setMsg('Push failed: ' + (e.response?.data?.error || e.message)); }
    finally { setBusy(null); }
  }
  async function unlink(sku, relink) {
    setBusy(sku);
    try { await api.post(`/amazon/unlink/${encodeURIComponent(sku)}`, { relink }); await load(); }
    finally { setBusy(null); }
  }

  if (!data) return <div className="loading">Loading Amazon eligibility…</div>;
  const s = data.summary || {};
  const rows = (data.rows || []).filter(r => {
    if (view === 'eligible' && !(r.eligible && !r.amz_status)) return false;
    if (view === 'listed'   && !r.amz_status) return false;
    if (view === 'skipped'  && r.eligible) return false;
    if (reason !== 'all' && reasonKey(r.reason) !== reason) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      if (!(r.sku || '').toLowerCase().includes(q) && !(r.title || '').toLowerCase().includes(q) && !(r.asin || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const pushable = (data.rows || []).filter(r => r.eligible && r.quantity > 0 && !['active', 'pending', 'unlinked'].includes(r.amz_status));

  return (
    <div>
      <div className="page-header">
        <div className="page-title">📦 Amazon</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={() => runGate(false)} disabled={!!busy || !data.configured}>🔍 Run eligibility gate</button>
          <button className="btn btn-ghost" onClick={() => runGate(true)} disabled={!!busy || !data.configured} title="Ignore cached ASIN matches and re-ask Amazon for everything">↻ Full re-check</button>
          <button className="btn btn-ghost" disabled={!!busy || !data.configured}
            title="Re-test every gated item against Amazon and push the ones whose approval came through (also runs daily at 6:30am)"
            onClick={async () => { setBusy('gated'); try { await api.post('/amazon/retry-gated', {}); setMsg('Re-testing gated items in the background — approved ones are pushed automatically; refresh in a few minutes.'); } finally { setBusy(null); } }}>
            🔓 Push newly approved
          </button>
          <button className="btn btn-primary" onClick={() => push(null, `all ${pushable.length} eligible items`)} disabled={!!busy || !pushable.length || !data.configured}>
            🚀 Push all eligible ({pushable.length})
          </button>
        </div>
      </div>

      {!data.configured && <div className="card" style={{ marginBottom: 12, color: 'var(--danger)' }}>Amazon credentials are not configured on the server.</div>}
      {msg && <div className="card" style={{ marginBottom: 12 }}>{msg} <button className="btn btn-ghost" style={{ marginLeft: 8 }} onClick={() => setMsg(null)}>✕</button></div>}

      <div className="dash-kpi-grid" style={{ marginBottom: 16 }}>
        <div className="stat-card"><div className="stat-value" style={{ color: 'var(--accent2)' }}>{s.eligible ?? 0}</div><div className="stat-label">Eligible of {s.total ?? 0} checked</div></div>
        <div className="stat-card"><div className="stat-value">{s.live ?? 0}</div><div className="stat-label">Live on Amazon</div></div>
        <div className="stat-card"><div className="stat-value" style={{ color: 'var(--warn)' }}>{s.pending ?? 0}</div><div className="stat-label">Submitted, awaiting Amazon</div></div>
        <div className="stat-card"><div className="stat-value" style={{ color: s.errors > 0 ? 'var(--danger)' : undefined }}>{s.errors ?? 0}</div><div className="stat-label">Rejected</div></div>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {[['eligible', 'Eligible, not yet listed'], ['listed', 'On Amazon'], ['skipped', 'Skipped'], ['all', 'All']].map(([k, l]) => (
            <button key={k} className={`inv-filter-btn ${view === k ? 'active' : ''}`} onClick={() => setView(k)}>{l}</button>
          ))}
          <select value={reason} onChange={e => setReason(e.target.value)} style={{ marginLeft: 12 }}>
            <option value="all">All reasons</option>
            {(data.reasons || []).map(r => <option key={r.reason} value={r.reason}>{reasonLabel(r.reason)} ({r.n})</option>)}
          </select>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="SKU, title or ASIN…" style={{ flex: 1, minWidth: 200, marginLeft: 'auto', maxWidth: 320 }} />
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>
          Last gate run: {data.lastRun ? `${fmtTime(data.lastRun.finished_at)} — ${data.lastRun.message}` : 'never'}. Re-checked automatically every Sunday 5am.
          Ungating a brand in Seller Central (“Apply to sell”) is picked up on the next run.
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="empty-state"><div className="empty-icon">—</div>Nothing matches this view.</div>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>SKU</th><th>Title</th><th>ASIN</th><th>Condition</th>
                  <th style={{ textAlign: 'right' }}>Qty</th><th style={{ textAlign: 'right' }}>Master $</th><th style={{ textAlign: 'right' }}>Amazon $</th>
                  <th>Gate</th><th>Amazon status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const st = r.amz_status;
                  const stColor = st === 'active' ? 'var(--accent2)' : st === 'pending' ? 'var(--warn)' : st === 'error' ? 'var(--danger)' : 'var(--muted)';
                  return (
                    <tr key={r.sku} style={st === 'error' ? { background: 'rgba(255,77,109,.06)' } : undefined}>
                      <td><Link to={`/inventory?search=${encodeURIComponent(r.sku)}`} className="mono" style={{ color: 'var(--accent2)', textDecoration: 'none' }}>{r.sku}</Link></td>
                      <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.amz_title ? `Amazon: ${r.amz_title}` : ''}>
                        {r.title || '—'}
                        {r.amz_title && <div style={{ fontSize: 11, color: r.title_match != null && r.title_match <= 2 ? 'var(--warn)' : 'var(--muted)' }}>{r.title_match != null && r.title_match <= 2 ? '⚠ ' : ''}Amazon: {r.amz_title}</div>}
                      </td>
                      <td className="mono">{r.asin ? <a href={r.channel_url || `https://www.amazon.com/dp/${r.asin}`} target="_blank" rel="noreferrer" style={{ color: 'var(--text2)' }}>{r.asin}</a> : '—'}</td>
                      <td>{COND_LABELS[r.condition_type] || (r.condition ? `(${r.condition})` : '—')}</td>
                      <td style={{ textAlign: 'right' }}>{r.quantity}{st === 'active' && r.amz_qty != null && r.amz_qty !== r.quantity ? <span style={{ color: 'var(--warn)' }} title="Amazon shows a different qty"> ({r.amz_qty})</span> : null}</td>
                      <td style={{ textAlign: 'right' }}>{r.price != null ? `$${Number(r.price).toFixed(2)}` : '—'}</td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {priceEdit?.sku === r.sku ? (
                          <span>
                            <input type="number" step="0.01" min="0" autoFocus value={priceEdit.value} placeholder="auto"
                              onChange={e => setPriceEdit({ sku: r.sku, value: e.target.value })}
                              onKeyDown={e => { if (e.key === 'Enter') savePrice(); if (e.key === 'Escape') setPriceEdit(null); }}
                              style={{ width: 80, textAlign: 'right' }} />
                            <button className="btn btn-ghost" style={{ fontSize: 11, padding: '1px 6px', marginLeft: 4 }} disabled={busy === 'price'} onClick={savePrice}>Save</button>
                          </span>
                        ) : (
                          <span title={r.amazon_price != null ? 'Fixed Amazon price (override). Click to change; clear to go back to automatic.' : 'Automatic: master × 1.10. Click to set a fixed Amazon price.'}
                            onClick={() => setPriceEdit({ sku: r.sku, value: r.amazon_price != null ? String(r.amazon_price) : '' })}
                            style={{ cursor: 'pointer', borderBottom: '1px dashed var(--muted)', color: r.amazon_price != null ? 'var(--warn)' : undefined }}>
                            {r.amz_price != null ? `$${Number(r.amz_price).toFixed(2)}` : '—'}{r.amazon_price != null ? ' ✎' : ''}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className="badge" style={{ background: r.eligible ? 'rgba(0,212,170,.12)' : 'var(--surface2)', color: r.eligible ? 'var(--accent2)' : 'var(--text2)' }} title={r.reason}>
                          {reasonLabel(r.reason)}
                        </span>
                      </td>
                      <td>
                        {st ? <span className="badge" style={{ background: 'var(--surface2)', color: stColor }} title={r.amz_error || ''}>{st === 'active' ? 'Live' : st === 'pending' ? 'Submitted' : st === 'error' ? 'Rejected' : st}</span> : <span style={{ color: 'var(--muted)' }}>—</span>}
                        {st === 'error' && r.amz_error && <div style={{ fontSize: 11, color: 'var(--danger)', maxWidth: 260, whiteSpace: 'normal' }}>{r.amz_error.slice(0, 180)}</div>}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {r.eligible && r.quantity > 0 && !['active', 'pending', 'unlinked'].includes(st) && (
                          <button className="btn btn-ghost" disabled={!!busy} onClick={() => push([r.sku], r.sku)}>Push</button>
                        )}
                        {['active', 'pending', 'error'].includes(st) && <button className="btn btn-ghost" disabled={!!busy} onClick={() => unlink(r.sku, false)} title="Stop syncing this SKU to Amazon">Unlink</button>}
                        {st === 'unlinked' && <button className="btn btn-ghost" disabled={!!busy} onClick={() => unlink(r.sku, true)}>Relink</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
